/**
 * The company profile.
 *
 * It is what a seeker sees beside every listing, and the lifetime spend on it
 * is public, so the form asks for what a candidate would want to know rather
 * than what a CRM would.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import {
  COMPANY_SIZE_BANDS,
  FUNDING_STAGES,
  FUNDING_STAGE_LABELS,
  JD_LIMITS,
  type Company,
  type CompanyProfileInput,
  type CompanySizeBand,
  type FundingStage,
} from '@bidtohire/shared';

import { useAssetsBasePath } from '../lib/config';
import { assetUrl } from '../lib/format';
import { useLogoUpload } from '../lib/queries';
import { SelectField, TextAreaField, TextField } from './Fields';
import { errorMessage } from './Status';

interface CompanyFormState {
  name: string;
  websiteUrl: string;
  careersUrl: string;
  blurb: string;
  hqLocation: string;
  industry: string;
  sizeBand: CompanySizeBand | '';
  fundingStage: FundingStage | '';
  billingContactEmail: string;
  linkedin: string;
  x: string;
  github: string;
  logoKey: string;
}

function initialState(company: Company | undefined): CompanyFormState {
  return {
    name: company?.name ?? '',
    websiteUrl: company?.websiteUrl ?? '',
    careersUrl: company?.careersUrl ?? '',
    blurb: company?.blurb ?? '',
    hqLocation: company?.hqLocation ?? '',
    industry: company?.industry ?? '',
    sizeBand: company?.sizeBand ?? '',
    fundingStage: company?.fundingStage ?? '',
    billingContactEmail: '',
    linkedin: company?.socials?.linkedin ?? '',
    x: company?.socials?.x ?? '',
    github: company?.socials?.github ?? '',
    logoKey: company?.logoKey ?? '',
  };
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function CompanyForm({
  company,
  submitLabel,
  busy,
  serverError,
  onSubmit,
}: {
  readonly company: Company | undefined;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly serverError?: string | undefined;
  readonly onSubmit: (input: CompanyProfileInput) => void | Promise<void>;
}): ReactNode {
  const [state, setState] = useState<CompanyFormState>(() => initialState(company));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const upload = useLogoUpload();
  const basePath = useAssetsBasePath();

  function update<K extends keyof CompanyFormState>(key: K, value: CompanyFormState[K]): void {
    setState((previous) => ({ ...previous, [key]: value }));
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    const found: Record<string, string> = {};

    const name = state.name.trim();
    if (name === '') found['name'] = 'Enter your company name.';
    else if (name.length > 80) found['name'] = 'Company names are at most 80 characters.';

    for (const key of ['websiteUrl', 'careersUrl'] as const) {
      const value = state[key].trim();
      if (value !== '' && !isHttpUrl(value)) {
        found[key] = 'Enter a full link, beginning http:// or https://.';
      }
    }

    if (state.blurb.trim().length > JD_LIMITS.companyBlurbMaxLength) {
      found['blurb'] = `Keep this to ${JD_LIMITS.companyBlurbMaxLength} characters.`;
    }

    const email = state.billingContactEmail.trim();
    if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      found['billingContactEmail'] = 'Enter a valid email address.';
    }

    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const socials = {
      ...(state.linkedin.trim() === '' ? {} : { linkedin: state.linkedin.trim() }),
      ...(state.x.trim() === '' ? {} : { x: state.x.trim() }),
      ...(state.github.trim() === '' ? {} : { github: state.github.trim() }),
    };

    const input: CompanyProfileInput = {
      name,
      ...(state.websiteUrl.trim() === '' ? {} : { websiteUrl: state.websiteUrl.trim() }),
      ...(state.careersUrl.trim() === '' ? {} : { careersUrl: state.careersUrl.trim() }),
      ...(state.blurb.trim() === '' ? {} : { blurb: state.blurb.trim() }),
      ...(state.logoKey === '' ? {} : { logoKey: state.logoKey }),
      ...(state.hqLocation.trim() === '' ? {} : { hqLocation: state.hqLocation.trim() }),
      ...(state.industry.trim() === '' ? {} : { industry: state.industry.trim() }),
      ...(state.sizeBand === '' ? {} : { sizeBand: state.sizeBand }),
      ...(state.fundingStage === '' ? {} : { fundingStage: state.fundingStage }),
      ...(Object.keys(socials).length === 0 ? {} : { socials }),
      ...(email === '' ? {} : { billingContactEmail: email }),
    };

    void onSubmit(input);
  }

  const logoPreview = assetUrl(basePath, state.logoKey === '' ? undefined : state.logoKey);

  return (
    <form className="stack" onSubmit={submit} noValidate>
      <section className="card formsection">
        <h2>Company profile</h2>

        <TextField
          label="Company name"
          value={state.name}
          onChange={(value) => update('name', value)}
          maxLength={80}
          error={errors['name']}
          required
        />

        <div className="fieldrow">
          <div className="col">
            <TextField
              label="Website"
              type="url"
              value={state.websiteUrl}
              onChange={(value) => update('websiteUrl', value)}
              placeholder="https://acme.com"
              error={errors['websiteUrl']}
            />
          </div>
          <div className="col">
            <TextField
              label="Careers page"
              type="url"
              value={state.careersUrl}
              onChange={(value) => update('careersUrl', value)}
              error={errors['careersUrl']}
            />
          </div>
        </div>

        <TextAreaField
          label="About the company"
          value={state.blurb}
          onChange={(value) => update('blurb', value)}
          rows={4}
          maxLength={JD_LIMITS.companyBlurbMaxLength}
          hint={`Shown beside every listing. Up to ${JD_LIMITS.companyBlurbMaxLength} characters.`}
          error={errors['blurb']}
        />

        <div className="fieldrow">
          <div className="col">
            <TextField
              label="Headquarters"
              value={state.hqLocation}
              onChange={(value) => update('hqLocation', value)}
              placeholder="Chicago, IL"
            />
          </div>
          <div className="col">
            <TextField
              label="Industry"
              value={state.industry}
              onChange={(value) => update('industry', value)}
            />
          </div>
          <div className="col-sm">
            <SelectField
              label="Size"
              value={state.sizeBand}
              onChange={(value: CompanySizeBand) => update('sizeBand', value)}
              options={COMPANY_SIZE_BANDS.map((value) => ({ value, label: `${value} people` }))}
              placeholder="Not stated"
            />
          </div>
          <div className="col-sm">
            <SelectField
              label="Funding stage"
              value={state.fundingStage}
              onChange={(value: FundingStage) => update('fundingStage', value)}
              options={FUNDING_STAGES.map((value) => ({
                value,
                label: FUNDING_STAGE_LABELS[value],
              }))}
              placeholder="Not stated"
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="logo-input">Logo</label>
          <div className="row">
            {logoPreview === undefined ? null : (
              <img className="logo is-large" src={logoPreview} alt="" width={56} height={56} />
            )}
            <input
              id="logo-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file === undefined) return;
                upload.mutate(file, { onSuccess: (key) => update('logoKey', key) });
              }}
            />
          </div>
          <span className="hint">Square PNG, JPEG, or WebP, up to 2 MB.</span>
          {upload.isPending ? <span className="hint">Uploading…</span> : null}
          {upload.isError ? <span className="error">{errorMessage(upload.error)}</span> : null}
        </div>

        <TextField
          label="Billing contact email"
          type="email"
          value={state.billingContactEmail}
          onChange={(value) => update('billingContactEmail', value)}
          hint="Where Stripe receipts are sent. Never shown publicly."
          error={errors['billingContactEmail']}
        />
      </section>

      <section className="card formsection">
        <h2>Social profiles</h2>
        <div className="fieldrow">
          <div className="col">
            <TextField
              label="LinkedIn"
              value={state.linkedin}
              onChange={(value) => update('linkedin', value)}
            />
          </div>
          <div className="col">
            <TextField label="X" value={state.x} onChange={(value) => update('x', value)} />
          </div>
          <div className="col">
            <TextField
              label="GitHub"
              value={state.github}
              onChange={(value) => update('github', value)}
            />
          </div>
        </div>
      </section>

      {serverError === undefined ? null : (
        <div className="notice is-error" role="alert">
          {serverError}
        </div>
      )}

      <div className="formactions">
        <button type="submit" className="cta is-compact" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
